/**
 * voyagePlanResolution.js — Sub-ticket B
 *
 * Resolve a DRAFT voyage's `draftSelectedPlanId` against the vessel's current
 * voyage-plan library at Let Go time and return the snapshot to write onto
 * the UNDERWAY voyage record.
 *
 * This module is intentionally:
 *   - SIDE-EFFECT FREE (pure JS, no React, no storage access)
 *   - SYNCHRONOUS (no Promises)
 *   - TDZ-SAFE (module scope; the consumer at useVoyages.js imports it at
 *     the top of the file, never re-declares inside a hook body — see
 *     CODING-BRIEF-B.md §4.5).
 *
 * The library and operations list are READ by the CALLER and passed in.
 * That separation keeps this helper trivially unit-testable (no React
 * test renderer, no mocking of useStorageState).
 *
 * Output:
 *   - resolution: status enum + audit fields (always non-null)
 *   - snapshot:   full VoyagePlanSnapshot when status === 'attached', else null
 *   - items:      { [itemId]: { status: 'pending' } } for every snapshotted item
 *
 * Resolution status enum (CODING-BRIEF-B.md §2.3 — extends synthesis §3.4
 * with `'no_plan_selected'` so the field is never null on an UNDERWAY voyage):
 *
 *   'attached'          — happy path, snapshot written
 *   'source_deleted'    — DRAFT pointed at a planId that no longer exists
 *   'source_archived'   — plan exists but isArchived === true
 *   'empty_plan'        — plan exists but items[].length === 0
 *   'vessel_changed'    — defensive / effectively unreachable in v1 (see Note A below)
 *   'no_plan_selected'  — DRAFT had no plan picked
 *
 * Note A on `'vessel_changed'`: with `useStorageState` vessel-scoping
 * (useStorageState.js:111), a DRAFT lives on the vessel it was created on
 * and the libraryPlans the caller passes in are the CURRENT vessel's
 * library. So if the planId is found in `libraryPlans` we know it belongs
 * to this vessel. The branch is retained in the enum because synthesis §3.4
 * mandates all 5 states; in code it is effectively unreachable in v1.
 *
 * BACKWARD COMPATIBILITY: every read site of voyage.voyagePlanSnapshot /
 * voyagePlanResolution / voyagePlanItems MUST tolerate `undefined`
 * (pre-B voyages have none of these fields).
 */

/**
 * @typedef {'attached'|'source_deleted'|'source_archived'|'empty_plan'|'vessel_changed'|'no_plan_selected'} ResolutionStatus
 *
 * @typedef {Object} VoyagePlanResolution
 * @property {ResolutionStatus} status
 * @property {string} resolvedAt
 * @property {string|null} draftSelectedPlanId
 * @property {number} [planVersionAtLetGo]
 * @property {number|null} [draftSelectedPlanVersionAtSave]
 *
 * @typedef {Object} SnapshotWaypoint
 * @property {string} id
 * @property {'waypoint'} kind
 * @property {number} order
 * @property {string} label
 * @property {string} [instructionNote]
 *
 * @typedef {Object} SnapshotOperationRef
 * @property {string} id
 * @property {'operation_ref'} kind
 * @property {number} order
 * @property {string} operationId
 * @property {string} [displayLabel]
 * @property {{name: string, phaseType: string|null, checklistItems: any[]}|null} operationSnapshot
 *
 * @typedef {Object} VoyagePlanSnapshot
 * @property {string} planId
 * @property {string} planName
 * @property {number} planVersion
 * @property {string} snapshottedAt
 * @property {Array<SnapshotWaypoint|SnapshotOperationRef>} items
 */

/**
 * Resolve a DRAFT plan selection at Let Go.
 *
 * @param {string|null} draftSelectedPlanId — from the DRAFT voyage
 * @param {number|null} draftSelectedPlanVersionAtSave — captured at DRAFT save time
 * @param {Array} libraryPlans — voyage_settings.voyagePlans at Let Go time
 * @param {Array} operationsList — voyage_settings.operationsList at Let Go time
 * @param {string|null} currentVesselId — accepted for future surface extension; unused in v1 (see Note A)
 *
 * @returns {{
 *   resolution: VoyagePlanResolution,
 *   snapshot: VoyagePlanSnapshot|null,
 *   items: Record<string, {status: 'pending'}>,
 * }}
 */
export function resolvePlanForLetGo(
  draftSelectedPlanId,
  draftSelectedPlanVersionAtSave,
  libraryPlans,
  operationsList,
  currentVesselId // eslint-disable-line no-unused-vars
) {
  const resolvedAt = new Date().toISOString();

  // 'no_plan_selected' — DRAFT had no plan picked
  if (!draftSelectedPlanId) {
    return {
      resolution: {
        status: 'no_plan_selected',
        resolvedAt,
        draftSelectedPlanId: null,
      },
      snapshot: null,
      items: {},
    };
  }

  const plan = Array.isArray(libraryPlans)
    ? libraryPlans.find((p) => p?.id === draftSelectedPlanId)
    : null;

  // 'source_deleted' — plan no longer in library
  if (!plan) {
    return {
      resolution: {
        status: 'source_deleted',
        resolvedAt,
        draftSelectedPlanId,
      },
      snapshot: null,
      items: {},
    };
  }

  // 'source_archived' — plan exists but is archived
  if (plan.isArchived) {
    return {
      resolution: {
        status: 'source_archived',
        resolvedAt,
        draftSelectedPlanId,
      },
      snapshot: null,
      items: {},
    };
  }

  // 'empty_plan' — plan exists but has no items
  if (!Array.isArray(plan.items) || plan.items.length === 0) {
    return {
      resolution: {
        status: 'empty_plan',
        resolvedAt,
        draftSelectedPlanId,
      },
      snapshot: null,
      items: {},
    };
  }

  // 'attached' — happy path. Snapshot every item. For operation_ref items,
  // capture a FULL snapshot of the referenced operation's checklist so the
  // UNDERWAY voyage NEVER reads voyage_settings.voyagePlans again
  // (per synthesis P4.6 snapshot-read-site rule).
  const snapshotItems = plan.items.map((it, i) => {
    if (it?.kind === 'waypoint') {
      return {
        id: it.id,
        kind: 'waypoint',
        order: i,
        label: typeof it.label === 'string' ? it.label : '',
        instructionNote: typeof it.instructionNote === 'string' ? it.instructionNote : '',
      };
    }

    // operation_ref — snapshot the referenced operation (or null if missing)
    const op = Array.isArray(operationsList)
      ? operationsList.find((o) => o?.id === it.operationId)
      : null;

    return {
      id: it.id,
      kind: 'operation_ref',
      order: i,
      operationId: it.operationId,
      // Only include `displayLabel` when it's a non-empty string; consumers can
      // fall back to operationSnapshot.name without a nullish field.
      ...(it.displayLabel ? { displayLabel: it.displayLabel } : {}),
      operationSnapshot: op
        ? {
            name: op.name || op.label || it.displayLabel || 'Operation',
            phaseType: op.phaseType || null,
            // Shallow-copy the checks array so post-snapshot mutation of the
            // source operation in operationsList does NOT mutate this snapshot.
            // (Items inside are still shared by reference; the synthesis only
            //  requires that the canonical list survives — full deep-clone is
            //  overkill and would complicate any future structural change.)
            checklistItems: Array.isArray(op.checks) ? [...op.checks] : [],
          }
        : null,
    };
  });

  const items = snapshotItems.reduce((acc, it) => {
    acc[it.id] = { status: 'pending' };
    return acc;
  }, {});

  return {
    resolution: {
      status: 'attached',
      resolvedAt,
      draftSelectedPlanId,
      planVersionAtLetGo: typeof plan.version === 'number' ? plan.version : 1,
      draftSelectedPlanVersionAtSave:
        typeof draftSelectedPlanVersionAtSave === 'number'
          ? draftSelectedPlanVersionAtSave
          : null,
    },
    snapshot: {
      planId: plan.id,
      planName: typeof plan.name === 'string' && plan.name.length > 0
        ? plan.name
        : 'Untitled plan',
      planVersion: typeof plan.version === 'number' ? plan.version : 1,
      snapshottedAt: resolvedAt,
      items: snapshotItems,
    },
    items,
  };
}

export default resolvePlanForLetGo;
